import { useCallback, useState } from "react"
import { Alert, Button, Group, Paper, Stack, Tabs, Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { Trans, useTranslation } from "react-i18next"
import { DetailSection, InfoRow } from "../../../components/crud"
import { getErrorMessage } from "../utils"
import { useAssignRolePermissions } from "../hooks/useRoles"
import type { RoleDetail } from "../models/role"
import PermissionTree from "./PermissionTree"
import RoleMembersTabContent from "./RoleMembersTabContent"

interface Props {
  role: RoleDetail
}

export default function RoleDetailContent({ role }: Props) {
  const { t } = useTranslation()
  const assignPermMutation = useAssignRolePermissions()
  const [activeTab, setActiveTab] = useState<string | null>("info")
  const [pendingPermissions, setPendingPermissions] = useState<Set<string> | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const handlePermissionChange = useCallback((perms: Set<string>) => {
    setPendingPermissions(perms)
    setHasChanges(true)
  }, [])

  const handleSavePermissions = () => {
    if (!pendingPermissions) return

    const original = new Set(role.permissions.assigned)
    const add: string[] = []
    const remove: string[] = []

    for (const p of pendingPermissions) {
      if (!original.has(p)) add.push(p)
    }
    for (const p of original) {
      if (!pendingPermissions.has(p)) remove.push(p)
    }

    assignPermMutation.mutate(
      { name: role.name, add, remove },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("adminRoles.permissionsUpdateSuccess"),
          })
          setHasChanges(false)
          setPendingPermissions(null)
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      },
    )
  }

  return (
    <Tabs value={activeTab} onChange={setActiveTab}>
      <Tabs.List mb="md">
        <Tabs.Tab value="info" leftSection={<i className="fa fa-info-circle" />}>
          {t("adminRoles.info")}
        </Tabs.Tab>
        <Tabs.Tab value="permissions" leftSection={<i className="fa fa-key" />}>
          {t("adminRoles.permissions")}
        </Tabs.Tab>
        <Tabs.Tab value="members" leftSection={<i className="fa fa-users" />}>
          {t("adminRoles.members")} ({role.user_count})
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="info">
        <Stack gap="md">
          <DetailSection title={t("adminRoles.roleInfo")}>
            <Stack gap="xs">
              <InfoRow label={t("adminRoles.name")}>
                <Text size="sm">{role.name}</Text>
              </InfoRow>
              <InfoRow label={t("adminRoles.description")}>
                <Text size="sm">{role.description ?? <Trans i18nKey="common.notFilled" />}</Text>
              </InfoRow>
              <InfoRow label={t("adminRoles.ruleName")}>
                <Text size="sm">{role.rule_name ?? <Trans i18nKey="common.notFilled" />}</Text>
              </InfoRow>
              <InfoRow label={t("adminRoles.createdAt")}>
                <Text size="sm">
                  {role.created_at
                    ? new Date(role.created_at).toLocaleDateString("pt-PT", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : <Trans i18nKey="common.notFilled" />}
                </Text>
              </InfoRow>
            </Stack>
          </DetailSection>

          {role.child_roles.length > 0 && (
            <DetailSection title={t("adminRoles.inheritedRoles")}>
              <Group gap="xs">
                {role.child_roles.map((r) => (
                  <Text key={r} size="sm" component="span">
                    <i className="fa fa-shield" /> {r}
                  </Text>
                ))}
              </Group>
            </DetailSection>
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="permissions">
        <Stack>
          {assignPermMutation.isError && (
            <Alert color="red" withCloseButton onClose={() => assignPermMutation.reset()}>
              {assignPermMutation.error?.message}
            </Alert>
          )}
          <PermissionTree
            assignedPermissions={role.permissions.assigned}
            onChange={handlePermissionChange}
          />
          {hasChanges && (
            <Group justify="center" mt="md">
              <Button
                onClick={handleSavePermissions}
                loading={assignPermMutation.isPending}
                leftSection={<i className="fa fa-save" />}
              >
                <Trans i18nKey="common.saveChanges" />
              </Button>
            </Group>
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="members">
        <RoleMembersTabContent roleName={role.name} />
      </Tabs.Panel>
    </Tabs>
  )
}
